import {Accordion, AccordionDetails, AccordionSummary, Card, Grid} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {SwitchSegmentedYesNo, TextBox} from '../../../Core/UI';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import {SelectInputElement} from '../Components/SelectInputElement';
import React from 'react';
import {observer} from 'mobx-react-lite';
import {HealthQuestionsProps} from './HealthQuestionsList';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/HealthXref';

const RespiratoryDisorder = observer(({counter, valueChanged, parameters, coreQuestions}: HealthQuestionsProps) => {
    return (
        <Accordion style={{width: '100%'}} TransitionProps={{unmountOnExit: true}}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={'respiratoryDisorder-content'}
                id={'respiratoryDisorder-header'}
            >
                <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <div>Respiratory disorder</div>
                    <div>{counter('respiratoryDisorder')}</div>
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container>
                    <Grid key="params" container>
                        {coreQuestions?.map(({name, label}) => (
                            <Grid item xs={4} key={name}>
                                <SwitchSegmentedYesNo
                                    param={{
                                        name: name,
                                        label: label,
                                        category: 'respiratoryDisorder',
                                        ...(parameters[name as keyof typeof parameters] as Parameter),
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                />
                            </Grid>
                        ))}
                        {parameters.Asthma.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Asthma</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.asthmaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.asthmaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'What is your Asthma severity?',
                                                    name: 'asthmaSeverity',
                                                    options: fieldOptions.asthmaSeverity.options,
                                                    ...parameters.asthmaSeverity,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'asthmaHospitalizations',
                                                    label: 'Have you been hospitalized due to this condition within the last 5 years?',
                                                    ...parameters.asthmaHospitalizations,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'asthmaSteroids',
                                                    label: 'Have you required oral steroids (i.e. Prednisone) for this condition in the last 2 years?',
                                                    ...parameters.asthmaSteroids,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.COPD.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>COPD</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed?"
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.copdDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.copdDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'copdOxygenUsage',
                                                    label: 'Are you currently on Oxygen?',
                                                    ...parameters.copdOxygenUsage,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'copdSymptoms',
                                                    label: 'Are you currently experiencing symptoms related to this condition?',
                                                    ...parameters.copdSymptoms,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="copdMedications"
                                                label="Provide names of medications."
                                                value={parameters.copdMedications?.value}
                                                onChange={(value) => valueChanged(parameters.copdMedications, value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
});

export default RespiratoryDisorder;
